import ApiWrapper from '../../api/ApiWrapper';
import { pUiLog } from '../../api/models/.catalog';
import { EnvironmentVariables } from '../../constants/EnvironmentVariables';

export enum LogTypes {
  Log = 'Log',
  Debug = 'Debug',
  Info = 'Info',
  Warning = 'Warning',
  Error = 'Error',
  Critical = 'Critical',
}

type ILogTypes = LogTypes;
type ILogLevel = 0 | 1 | 2 | 3 | 4 | 5;
type ILogLevelSelector = ILogLevel | false;
type ILogObject = {
  name: ILogTypes;
  level: ILogLevel;
};
type ILoggingProperties = {
  time: string;
  stackTrace: string;
  appInfo: {
    IsMobile: boolean;
    VersionName: string;
    VersionCode: number;
    Env: string;
  };
  browserInfo: { BrowserUserAgent: string; BrowserData: string };
  deviceInfo: { MobileData: string };
  data: string;
};

class _LoggingService {
  private static _instance: _LoggingService;
  private static types = {
    Log: { name: LogTypes.Log, level: 1 as ILogLevel },
    Debug: { name: LogTypes.Debug, level: 0 as ILogLevel },
    Info: { name: LogTypes.Info, level: 2 as ILogLevel },
    Warning: { name: LogTypes.Warning, level: 3 as ILogLevel },
    Error: { name: LogTypes.Error as ILogTypes, level: 4 as ILogLevel },
    Critical: { name: LogTypes.Critical as ILogTypes, level: 5 as ILogLevel },
  };

  private CrashTheAppStartingFrom: ILogLevelSelector = false;
  private suppressDefaultLogBehavior: boolean = false;
  private suppressSendingReportMessage: boolean = false;
  private suppressOpenToastMessage: boolean = false;
  private SendReportStartingFrom: ILogLevelSelector = 4;
  private OpenToastStartingFrom: ILogLevelSelector = 5;

  private constructor() {
    const log = console.log;
    const debug = console.debug;
    const info = console.info;
    const warn = console.warn;
    const error = console.error;
    // window.onerror = function (
    //   msg: string | Event,
    //   url: string,
    //   lineNo: number,
    //   columnNo: number,
    //   error?: Error | undefined
    // ) {
    //   //handle error
    //   return false;
    // };
    console.log = (...data: any[]): void => {
      this.startActions(LogTypes.Log, data);
      if (!this.suppressDefaultLogBehavior) log.apply(console, data as any);
    };
    console.debug = (...data: any[]): void => {
      this.startActions(LogTypes.Debug, data);
      if (!this.suppressDefaultLogBehavior) debug.apply(console, data as any);
    };
    console.info = (...data: any[]): void => {
      this.startActions(LogTypes.Info, data);
      if (!this.suppressDefaultLogBehavior) info.apply(console, data as any);
    };
    console.warn = (...data: any[]): void => {
      this.startActions(LogTypes.Warning, data);
      if (!this.suppressDefaultLogBehavior) warn.apply(console, data as any);
    };
    console.error = (...data: any[]): void => {
      this.startActions(LogTypes.Error, data);
      if (!this.suppressDefaultLogBehavior) error.apply(console, data as any);
    };

    this.setOnGlobal();
  }

  public static Instance() {
    return this._instance || this.getFromGlobal() || (this._instance = new this());
  }

  public init = (): void => { }; //already inited, this call ensures

  private setOnGlobal(): void {
    if (typeof window !== 'undefined') {
      (window as any).LoggingService = this;
    }
  }

  private static getFromGlobal(): _LoggingService | undefined {
    if (typeof window !== 'undefined') {
      return (window as any).LoggingService;
    }
    return undefined;
  }

  private static typesArray = (): ILogObject[] => Object.values(_LoggingService.types);

  //#region Properties
  private getLoggingproperties = (data: any): ILoggingProperties => {
    return {
      time: this.createDate(),
      stackTrace: this.createStackTrace(),
      appInfo: this.getAppInfo(),
      browserInfo: this.getBrowserInfo(),
      deviceInfo: this.getDeviceInfo(),
      data: this.getData(data),
    };
  };

  private getData = (data: any): string => {
    try {
      return JSON.stringify(data);
    } catch (ex) {
      return ' ';
    }
  };

  private createDate = (): string => {
    return new Date().toISOString();
  };

  private createStackTrace = (): string => {
    const err = new Error();
    const stackArray = (err?.stack || '').split('\n');
    const stack = (err?.stack || '')
      .split('\n')
      .slice(Math.min(stackArray.length - 1, 5), stackArray.length)
      .join('\n');
    return 'CustomStack ' + stack;
  };

  private getAppInfo = (): {
    IsMobile: boolean;
    VersionName: string;
    VersionCode: number;
    Env: string;
  } => {
    try {
      return {
        IsMobile: EnvironmentVariables.Is_Mobile,
        VersionName: EnvironmentVariables.Build_Name,
        VersionCode: EnvironmentVariables.Build_Code,
        Env: EnvironmentVariables.EnvironmentType,
      };
    } catch (ex) {
      return { IsMobile: false, VersionName: '-', VersionCode: 0, Env: '-' };
    }
  };

  private getDeviceInfo = (): { MobileData: string } => {
    try {
      if (EnvironmentVariables.Is_Mobile) {
        return {
          MobileData: JSON.stringify({ Platform: EnvironmentVariables.Platform }),
        };
      } else {
        return { MobileData: '-' };
      }
    } catch (ex) {
      return { MobileData: '-' };
    }
  };

  private getBrowserInfo = (): { BrowserUserAgent: string; BrowserData: string } => {
    try {
      return {
        BrowserUserAgent: this.stringifyAssist(window.navigator.userAgent),
        BrowserData: JSON.stringify({
          cookieEnabled: this.stringifyAssist(window.navigator.cookieEnabled).toString(),
          supportsMultiTouch: (navigator.maxTouchPoints > 1).toString(),
          online: this.stringifyAssist(window.navigator.onLine).toString(),
          isAutomated: this.stringifyAssist(window.navigator.webdriver).toString(),
          hardwareConcurrency: this.stringifyAssist(
            window.navigator.hardwareConcurrency
          ).toString(),
          //permissions
          //deviceMemory
        }),
      };
    } catch (ex) {
      return {
        BrowserUserAgent: '-',
        BrowserData: '-',
      };
    }
  };

  private stringifyAssist = (input: any): any => {
    if (input === undefined) return 'undefined';
    if (input === null) return 'null';
    else return input;
  };
  //#endregion

  //#region Actions
  private startActions = (name: string, data: any): void => {
    const properties = this.getLoggingproperties(data);
    const object = _LoggingService.typesArray().find((i) => i.name === name);
    if (properties && object) {
      this.tryOpenToastAction(object);
      this.trySendReportAction(object, properties);
      this.crashTheAppAction(object);
    }
  };

  private dummyOperation = (): void => { };

  private tryOpenToastAction = (obj: ILogObject): void => {
    if (
      this.OpenToastStartingFrom &&
      !this.suppressOpenToastMessage &&
      obj.level &&
      obj.level >= this.OpenToastStartingFrom
    ) {
      try {
        //TODO hata mesajı ile toast aç
        this.dummyOperation();
      } catch (ex) {
        this.suppressOpenToastMessage = true;
      }
    }
  };

  private trySendReportAction = (obj: ILogObject, properties: ILoggingProperties): void => {
    if (
      this.SendReportStartingFrom &&
      !this.suppressSendingReportMessage &&
      obj.level &&
      obj.level >= this.SendReportStartingFrom
    ) {
      try {
        new ApiWrapper.User.UiLogEkle()
          .Request(
            new pUiLog({
              LogSeviyesi: obj.name.toString(),
              Mesaj: properties.data,
              StackTrace: properties.stackTrace,
              MobilMi: properties.appInfo.IsMobile,
              VersiyonIsmi: properties.appInfo.VersionName,
              VersiyonKodu: properties.appInfo.VersionCode,
              Ortam: properties.appInfo.Env,
              BrowserUserAgent: properties.browserInfo.BrowserUserAgent,
              BrowserOzellikleri: properties.browserInfo.BrowserData,
              MobilOzellikleri: properties.deviceInfo.MobileData,
            })
          )
          .catch((res) => {
            this.suppressSendingReportMessage = true;
          });
      } catch (ex) {
        this.suppressSendingReportMessage = true;
      }
    }
  };

  private crashTheAppAction = (obj: ILogObject): void => {
    if (obj.level && this.CrashTheAppStartingFrom && obj.level >= this.CrashTheAppStartingFrom) {
      setTimeout(function () {
        window.location.href = 'chrome://crash';
      }, 500);
    }
  };
  //#endregion
}

export const LoggingService = _LoggingService.Instance();
